"use client";

import { useState, useEffect, useTransition } from "react";
import Image from "next/image";
import { usePathname, useRouter, Link } from "@/i18n/navigation";
import { useTranslations, useLocale } from "next-intl";
import { Icon } from "./Icon";
import { LanguageSwitcher } from "./LanguageSwitcher";

export function Header() {
  const pathname = usePathname();
  const router = useRouter();
  const locale = useLocale();
  const t = useTranslations("nav");
  const [isPending, startTransition] = useTransition();

  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const navItems: Array<{ href: string; label: string; external?: boolean }> = [
    { href: "/platform", label: t("platform") },
    { href: "/modules", label: t("modules") },
    { href: "/specialized-emr", label: t("specializedEmr") },
    { href: "/ai-workflows", label: t("aiWorkflows") },
    { href: "/abdm", label: t("abdm") },
    { href: "/nhcx", label: t("nhcx") },
    { href: "/about", label: t("about") },
  ];

  const isActive = (href: string) =>
    pathname === href || (href !== "/" && pathname.startsWith(href));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const selectLanguage = (code: string) => {
    setOpen(false);
    startTransition(() => {
      router.replace(pathname, { locale: code });
    });
  };

  return (
    <>
      <header className={`site-header${scrolled ? " is-scrolled" : ""} locale-${locale}`}>
        <div className="hd-inner">

          {/* Brand: Chikitsa logo | divider | Vigorus logo only */}
          <Link href="/" className="hd-brand" aria-label="Chikitsa home">
            <Image
              src="/assets/logo-chikitsa.svg"
              alt="Chikitsa"
              width={135}
              height={32}
              priority
              className="hd-logo-chikitsa"
            />
            <span className="hd-sep" aria-hidden="true" />
            <Image
              src="/assets/logo-vigorus.png"
              alt="Vigorus AI"
              width={88}
              height={22}
              className="hd-logo-vigorus"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hd-nav" aria-label="Primary">
            {navItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className={`hd-link${isActive(item.href) ? " hd-link--on" : ""}`}
                target={item.external ? "_blank" : undefined}
                rel={item.external ? "noopener noreferrer" : undefined}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hd-cta">
            <LanguageSwitcher />
            <Link href="/contact" className="hd-contact">{t("contact")}</Link>
            <a
              href="https://calendar.app.google/Y8SLAw4AxTzNt3YL7"
              className="btn btn--brand hd-demo"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("bookDemo")}
            </a>
          </div>

          {/* Hamburger */}
          <button
            className="hd-ham"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            type="button"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? (
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <path d="M4 7h16M4 12h16M4 17h16" />
              </svg>
            )}
          </button>
        </div>
      </header>

      {/* Mobile Backdrop Overlay */}
      {open && (
        <div
          className="hd-mob__backdrop"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile overlay - deep blue like reference site */}
      <nav
        className={`hd-mob${open ? " hd-mob--open" : ""}`}
        aria-label="Mobile navigation"
        aria-hidden={!open}
        inert={!open ? true : undefined}
      >
        <div className="hd-mob__header">
          <Link href="/" className="hd-brand" onClick={() => setOpen(false)}>
            <Image
              src="/assets/logo-chikitsa.svg"
              alt="Chikitsa"
              width={145}
              height={34}
              priority
              className="hd-logo-chikitsa-mob"
              style={{ filter: "brightness(0) invert(1)", height: 34, width: "auto" }}
            />
          </Link>
          <button
            className="hd-mob__close"
            onClick={() => setOpen(false)}
            aria-label="Close menu"
            type="button"
          >
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <ul className="hd-mob__list">
          {navItems.map((item) => (
            <li key={item.href}>
              <Link
                href={item.href}
                className={`hd-mob__link${isActive(item.href) ? " hd-mob__link--on" : ""}`}
                onClick={() => setOpen(false)}
                target={item.external ? "_blank" : undefined}
                rel={item.external ? "noopener noreferrer" : undefined}
              >
                {item.label}
                <Icon name="arrow" />
              </Link>
            </li>
          ))}
          <li>
            <Link href="/contact" className="hd-mob__link" onClick={() => setOpen(false)}>
              {t("contact")} <Icon name="arrow" />
            </Link>
          </li>
        </ul>

        {/* Mobile Language Switcher Grid */}
        <div className="hd-mob__lang">
          <span className="hd-mob__lang-title">Choose Language / भाषा चुनें</span>
          <div className="hd-mob__lang-grid">
            {[
              { code: "en", label: "US", native: "English" },
              { code: "hi", label: "IN", native: "हिंदी" },
              { code: "ar", label: "SA", native: "العربية" },
              { code: "es", label: "ES", native: "Español" },
              { code: "fr", label: "FR", native: "Français" }
            ].map((lang) => {
              const isLangActive = lang.code === locale;
              return (
                <button
                  key={lang.code}
                  className={`hd-mob__lang-btn ${isLangActive ? "active" : ""}`}
                  onClick={() => selectLanguage(lang.code)}
                  disabled={isPending}
                >
                  <span className="hd-mob__lang-flag">{lang.label}</span>
                  <span className="hd-mob__lang-text">{lang.native}</span>
                </button>
              );
            })}
          </div>
        </div>
      </nav>
    </>
  );
}
